import { useMemo } from "react";

import { AnimatePresence, motion } from "framer-motion";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { useUser } from "@components/user-context";
import {
  INHERIT_BUDGET_MIX_KPI,
  useMMMBudgetMixKpiId,
} from "@features/mmm/hooks/mmm-url-state";
import { useCacDataState } from "@features/mmm/hooks/use-cac-data-state";
import { useRoasDataState } from "@features/mmm/hooks/use-roas-data-state";
import {
  formatCurrencyExact,
  formatCurrencyToDollarAmount,
  formatPotentiallyInfiniteNumber,
  getCurrencySymbol,
  roundPotentiallyInfiniteNumber,
} from "@features/mmm/utils/formatters";
import {
  calculateCac,
  calculateRoas,
  type BudgetMixBreakdownSpendDatum,
} from "@north-beam/nb-common";
import { ExploreBudgetsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import { MMMTab } from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { useBudgetMixes } from "@pages/mmm/hooks/use-budget-mixes";
import { useClientKpis } from "@pages/mmm/hooks/use-client-kpis";
import { useClientSettingData } from "@pages/mmm/hooks/use-client-setting-data";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";

import { type HistoricalBudgetMixResponse } from "../budget-mix-card-container/card-container";
import { type Kpi, KpiType } from "../client-setting/types";
import { useShowReferencePeriod } from "../hooks/use-show-reference-period";

import { BudgetMixCardMode } from "./card";
import { CardWrapper } from "./card-wrapper";
import { ChartTableSection } from "./chart-table-section";
import { Footer } from "./footer";
import { HistoricalValueSummarySection } from "./historical-value-summary-section";
import { MainMetricSummarySection } from "./main-metric-summary-section";
import { MetricBox } from "./metric-box";
import { HistoricalTitleSection } from "./title-section/historical-title-section";

type Props = {
  chartData: BudgetMixBreakdownSpendDatum[];
  kpiData: Record<string, number>;
  spend: number;
  mode: BudgetMixCardMode;
  loading: boolean;
  historicalData: HistoricalBudgetMixResponse;
};

export function HistoricalBudgetMixCard({
  chartData,
  kpiData,
  spend,
  loading,
  mode,
  historicalData,
}: Props) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.EXPLORE_BUDGETS);
  const { user } = useUser();
  const { selectedBudgetMixKpiId } = useMMMBudgetMixKpiId();
  const { kpis: clientKpis } = useClientKpis();
  const { clientSettings } = useClientSettings();
  const { getKpi: getClientSettingKpi } = useClientSettingData();
  const [budgetMixes] = useBudgetMixes();
  const [cacDataState, setCacDataState] = useCacDataState();
  const [roasDataState, setRoasDataState] = useRoasDataState();

  const overallKpi: Kpi | null = useMemo(() => {
    let result = null;

    if (selectedBudgetMixKpiId === INHERIT_BUDGET_MIX_KPI) {
      const adoptedBudgetMix = budgetMixes.find((e) => e.is_adopted);
      const firstBudgetMix = budgetMixes[0];
      const firstClientSetting = clientSettings[0];

      if (!result && adoptedBudgetMix) {
        result = clientKpis.find(
          (kpi) => kpi.kpi_name === adoptedBudgetMix.data.kpi_name,
        );

        if (!result) {
          result = clientKpis.find(
            (kpi) => kpi.kpi_name === adoptedBudgetMix.client_setting_kpi,
          );
        }
      }

      if (!result && firstBudgetMix) {
        result = clientKpis.find(
          (kpi) => kpi.kpi_name === firstBudgetMix.data.kpi_name,
        );

        if (!result) {
          result = clientKpis.find(
            (kpi) => kpi.kpi_name === firstBudgetMix.client_setting_kpi,
          );
        }
      }

      if (!result && firstClientSetting) {
        result = clientKpis.find(
          (kpi) => kpi.id === getClientSettingKpi(firstClientSetting)?.id,
        );
      }

      if (!result) {
        result = clientKpis.find((kpi) => kpi.is_default_kpi);
      }
    }

    if (!result) {
      result = clientKpis.find((kpi) => kpi.id === selectedBudgetMixKpiId);
    }

    if (!result) {
      result = clientKpis[0];
    }

    return result;
  }, [
    selectedBudgetMixKpiId,
    clientKpis,
    clientSettings,
    budgetMixes,
    getClientSettingKpi,
  ]);

  const overallDisplayMetricValue = useMemo(() => {
    return kpiData[overallKpi?.kpi_name];
  }, [overallKpi?.kpi_name, kpiData]);

  const selectedCacKpi = useMemo(() => {
    if (cacDataState === INHERIT_BUDGET_MIX_KPI) {
      if (overallKpi && overallKpi.kpi_type === KpiType.TRANSACTIONS) {
        return overallKpi.id;
      } else {
        return INHERIT_BUDGET_MIX_KPI;
      }
    }
    const selectedKpi = clientKpis.find((kpi) => kpi.id === cacDataState);
    if (selectedKpi?.kpi_type === KpiType.TRANSACTIONS) {
      return cacDataState;
    }
    return INHERIT_BUDGET_MIX_KPI;
  }, [cacDataState, overallKpi, clientKpis]);

  const selectedRoasKpi = useMemo(() => {
    if (roasDataState === INHERIT_BUDGET_MIX_KPI) {
      if (overallKpi && overallKpi.kpi_type === KpiType.REVENUE) {
        return overallKpi.id;
      } else {
        return INHERIT_BUDGET_MIX_KPI;
      }
    }
    const selectedKpi = clientKpis.find((kpi) => kpi.id === roasDataState);
    if (selectedKpi?.kpi_type === KpiType.REVENUE) {
      return roasDataState;
    }
    return INHERIT_BUDGET_MIX_KPI;
  }, [roasDataState, overallKpi, clientKpis]);

  const cacPillData = useMemo(() => {
    const getCacText = (value: number) => {
      const infFormatter = (inf: string) =>
        `${getCurrencySymbol(user.currencies.currentCurrency)}${inf}`;
      if (String(Math.round(value)).length > 1) {
        const formatter = (val: number) =>
          formatCurrencyToDollarAmount(val, user.currencies.currentCurrency);
        return `${formatPotentiallyInfiniteNumber(
          value,
          formatter,
          infFormatter,
        )}`;
      }

      const formatter = (val: number) =>
        formatCurrencyExact(val, user.currencies.currentCurrency);
      return `${formatPotentiallyInfiniteNumber(
        value,
        formatter,
        infFormatter,
      )}`;
    };
    const cacCompatibleKpis = clientKpis.filter(
      (kpi) => kpi.kpi_type === KpiType.TRANSACTIONS,
    );

    return [
      ...cacCompatibleKpis.map((kpi) => {
        const kpiValue = kpiData[kpi.kpi_name];
        if (!kpiValue) {
          return {
            key: kpi.id,
            displayLabel: kpi.friendly_name,
            displayValue: "",
            shouldShowAsOption: false,
            type: KpiType.TRANSACTIONS,
          };
        }
        const cac = calculateCac({ transactions: kpiValue, spend });

        return {
          key: kpi.id,
          displayLabel: kpi.friendly_name,
          displayValue: getCacText(cac),
          shouldShowAsOption: true,
          type: KpiType.TRANSACTIONS,
        };
      }),
      {
        key: INHERIT_BUDGET_MIX_KPI,
        displayLabel: "Not set",
        displayValue: "",
        shouldShowAsOption: false,
        type: KpiType.TRANSACTIONS,
      },
    ];
  }, [user.currencies.currentCurrency, clientKpis, kpiData, spend]);

  const roasPillData = useMemo(() => {
    const getRoasText = (value: number) => {
      return `${roundPotentiallyInfiniteNumber(value, 2)}x`;
    };

    const roasCompatibleKpis = clientKpis.filter(
      (kpi) => kpi.kpi_type === KpiType.REVENUE,
    );

    return [
      ...roasCompatibleKpis.map((kpi) => {
        const kpiValue = kpiData[kpi.kpi_name];
        if (!kpiValue) {
          return {
            key: kpi.id,
            displayLabel: kpi.friendly_name,
            displayValue: "",
            shouldShowAsOption: false,
            type: KpiType.REVENUE,
          };
        }

        const roas = calculateRoas({ revenue: kpiValue, spend });
        return {
          key: kpi.id,
          displayLabel: kpi.friendly_name,
          displayValue: getRoasText(roas),
          shouldShowAsOption: true,
          type: KpiType.REVENUE,
        };
      }),
      {
        key: INHERIT_BUDGET_MIX_KPI,
        displayLabel: "Not set",
        displayValue: "",
        shouldShowAsOption: false,
        type: KpiType.REVENUE,
      },
    ];
  }, [spend, clientKpis, kpiData]);

  const enableMmmReferencePeriod = useShowReferencePeriod();

  return (
    <CardWrapper>
      <AnimatePresence>
        {loading && (
          <motion.div
            className="absolute top-0 bottom-0 left-0 right-0 z-10 flex-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute top-0 bottom-0 left-0 right-0 bg-v2-secondary opacity-60"></div>
            <img
              className="h-24 z-1"
              src={salesPlatformColumnLoading}
              alt="Loading table"
            />
          </motion.div>
        )}
      </AnimatePresence>
      {enableMmmReferencePeriod && <div className="h-[2.35rem] "></div>}
      <HistoricalTitleSection historicalData={historicalData} />
      <MainMetricSummarySection>
        <HistoricalValueSummarySection
          historicalValue={overallDisplayMetricValue}
          kpi={overallKpi}
        />
      </MainMetricSummarySection>
      <div className="flex items-center w-full border-b border-v2-secondary">
        <MetricBox
          className="border-r border-v2-secondary w-2/5 h-16"
          value="spend"
          options={[
            {
              key: "spend",
              displayLabel: "Spend",
              displayValue: formatCurrencyToDollarAmount(
                spend,
                user.currencies.currentCurrency,
              ),
              shouldShowAsOption: true,
              type: "spend",
            },
          ]}
          isHistorical
        />
        {overallKpi?.kpi_type === KpiType.TRANSACTIONS ? (
          <MetricBox
            className="w-3/5 h-16"
            value={selectedCacKpi}
            options={cacPillData}
            isHistorical
          />
        ) : (
          <MetricBox
            className="w-3/5 h-16"
            value={selectedRoasKpi}
            options={roasPillData}
            isHistorical
          />
        )}
      </div>
      <ChartTableSection
        chartData={chartData}
        isHistorical
        useChart={mode === BudgetMixCardMode.CHART}
      />
      <Footer />
    </CardWrapper>
  );
}
